import React from 'react';
import { TFunction } from 'i18next';
import { AuthErrorCodes } from 'firebase/auth';
import { Timestamp } from 'firebase/firestore';
import { ReviewModel } from '../models/Review';
import { Paniers } from '../models/Paniers';
import { BasketListViewModel } from '../models/BasketListViewModel';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { Commandes } from '../models/Commandes';
import { OrderSatisfaction } from '../models/OrderSatisfaction';
import { ClientTableData } from '../models/ClientTableData';
import { SalesTableViewData } from '../models/SalesTableViewData';

const imageKitBaseUrl = 'https://ik.imagekit.io/ttd0gzik4ov';
const firebaseStorageBaseUrl = 'https://firebasestorage.googleapis.com';

export const getImageKitUrlFrom = (url: string, width?: number, height?: number): string => {
    if (width && height) {
        return url.replace(
            firebaseStorageBaseUrl,
            `${imageKitBaseUrl}/tr:w-${width},h-${height},c-at_max`
        );
    }
    return url.replace(firebaseStorageBaseUrl, imageKitBaseUrl);
};

export const getAuthErrorMessage = (
    code: string,
    t: TFunction
): {
    email?: string;
    password?: string;
    error?: string;
} => {
    switch (code) {
        case AuthErrorCodes.USER_DELETED:
            return { email: t(code) };
        case AuthErrorCodes.INVALID_PASSWORD:
            return { password: t(code) };
        case AuthErrorCodes.EMAIL_EXISTS:
            return { email: t(code) };
        default:
            return { error: t(code) };
    }
};

export const validatePassword = (password: string | undefined): boolean => {
    const hasCaps = /[A-Z]/;
    const hasSmall = /[a-z]/;
    const hasDigit = /[0-9]/;
    const hasSpecialCharacter = /[-!$%^&*()_+|"='{}\\[\]:\/;<>?,.@#]/;
    let count = 0;
    if (password) {
        if (hasCaps.test(password)) {
            count++;
        }
        if (hasSmall.test(password)) {
            count++;
        }
        if (hasDigit.test(password)) {
            count++;
        }
        if (hasSpecialCharacter.test(password)) {
            count++;
        }
    }
    if (count >= 3) {
        return true;
    }
    return false;
};

export const validatePasswordForKeywords = (
    value: string | undefined,
    firstName: string,
    lastName: string,
    email: string
): boolean => {
    if (value) {
        const password = value.toLowerCase();
        if (
            (firstName.length > 0 && password.includes(firstName.toLowerCase())) ||
            (lastName.length > 0 && password.includes(lastName.toLowerCase())) ||
            (email.length > 0 && password.includes(email.split('@')[0].toLowerCase()))
        ) {
            return false;
        }
    }
    return true;
};

export const getDateFromTimestamp = (timestampData: Timestamp | undefined): string | undefined => {
    if (timestampData) {
        return new Date(timestampData.seconds * 1000).toLocaleDateString('fr');
    }
};

export const IsDateWithin24Hours = (date: Timestamp): boolean => {
    const now = Date.now();
    const createdAt = date.toMillis();
    const oneDay = 24 * 60 * 60 * 1000;
    return now - createdAt < oneDay;
};

export const convertSecondsToHours = (date: Timestamp): number[] => {
    const now = Date.now();
    const createdAt = date.toMillis();
    const timeDifference = now - createdAt;
    const oneDay = 24 * 60 * 60 * 1000;
    const ms = oneDay - timeDifference;
    let seconds = ms / 1000;
    const hours = seconds / 3600;
    seconds = seconds % 3600;
    const minutes = seconds / 60;
    return [Math.floor(hours), Math.floor(minutes)];
};

export const isDateInThisWeek = (date: Timestamp): boolean => {
    const newDate = new Date(date.seconds * 1000);
    const todayObj = new Date();
    const todayDate = todayObj.getDate();
    const todayDay = todayObj.getDay();
    const firstDayOfWeek = new Date(todayObj.setDate(todayDate - todayDay));
    const lastDayOfWeek = new Date(firstDayOfWeek);
    lastDayOfWeek.setDate(lastDayOfWeek.getDate() + 6);
    return newDate >= firstDayOfWeek && newDate <= lastDayOfWeek;
};

export const getMonthFromTimestamp = (timestampData: Timestamp): number => {
    return new Date(timestampData.seconds * 1000).getMonth();
};

export const getDayFromTimestamp = (timestampData: Timestamp): number => {
    return new Date(timestampData.seconds * 1000).getDay();
};

export const getReviewDetails = (satisfaction: OrderSatisfaction[]): ReviewModel => {
    let disliked = 0;
    let liked = 0;
    let neutral = 0;
    satisfaction.forEach((element) => {
        if (element.dislikedCount) {
            disliked += element.dislikedCount;
        }
        if (element.lovedCount) {
            liked += element.lovedCount;
        }
        if (element.neutralCount) {
            neutral += element.neutralCount;
        }
    });
    const total = disliked + liked + neutral;
    return {
        dislikedPercentage: total !== 0 ? Math.round((disliked / total ?? 0) * 100) : 0,
        neutralPercentage: total !== 0 ? Math.round((neutral / total) * 100) : 0,
        likedPercentage: total !== 0 ? Math.round((liked / total) * 100) : 100
    };
};

export const getBasketNames = (paniersList: Paniers[]): BasketListViewModel[] => {
    const basketNames: BasketListViewModel[] = [];
    if (paniersList) {
        paniersList.forEach((item) => {
            if (item.titre && item.idPanier) {
                basketNames.push({
                    id: item.idPanier,
                    title: item.titre
                });
            }
        });
    }
    return basketNames;
};

export const producerPartnerCount = (t: TFunction): string[] => {
    return [
        t('first'),
        t('second'),
        t('third'),
        t('fourth'),
        t('fifth'),
        t('sixth'),
        t('seventh'),
        t('eighth'),
        t('ninth'),
        t('tenth')
    ];
};

export const isDateInThisMonth = (date: Timestamp): boolean => {
    const newDate = new Date(date.seconds * 1000);
    const todayObj = new Date();
    if (newDate.getMonth() === todayObj.getMonth()) {
        return true;
    }
    return false;
};

export const timeDropdownRequiredToPrepareCustomerOrder = (t: TFunction): string[] => {
    return [
        t('fourHour'),
        t('sixHour'),
        t('twelveHour'),
        t('twentyFourHour'),
        t('thirtySixHour'),
        t('fourtyEightHour'),
        t('seventyTwoHour'),
        t('fourJHour'),
        t('sevenJHour')
    ];
};

export const getCheckedImage = (checked: boolean | undefined): JSX.Element => {
    if (checked) {
        return <CheckIcon sx={{ marginTop: '24px', marginRight: '8px', color: '#56A723' }} />;
    } else {
        return <ClearIcon sx={{ marginTop: '24px', marginRight: '8px', color: '#DD1414' }} />;
    }
};

export const sortListByDate = (result: Commandes[]): Commandes[] => {
    return result.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
            const keyA = a.createdAt?.seconds;
            const keyB = b.createdAt?.seconds;
            if (keyA && keyB && keyA < keyB) return -1;
            if (keyA && keyB && keyA > keyB) return 1;
            return 0;
        }
        return 0;
    });
};

export const getLatestDate = (result: Commandes[]): Timestamp | undefined => {
    const date = sortListByDate(result);
    if (date && date[0].createdAt) {
        return date[0].createdAt;
    }
};

export const getTotalAmount = (result: Commandes[]): number | undefined => {
    let amount = 0;
    result.forEach((item) => {
        item.baskets?.forEach((basket) => {
            if (basket.prix) {
                amount += basket.prix * basket.quantite;
            }
        });
    });
    return amount;
};

export const getAverageAmount = (result: Commandes[]): number | undefined => {
    let amount = 0;
    const months: number[] = [];
    result.forEach((item) => {
        if (item.createdAt && item.baskets) {
            const month = getMonthFromTimestamp(item.createdAt);
            if (!months.includes(month)) {
                months.push(month);
            }
            item.baskets.forEach((content) => {
                amount += content.prix * content.quantite;
            });
        }
    });
    return amount / months.length;
};

export const getMonths = (t: TFunction): string[] => {
    return [
        t('january'),
        t('february'),
        t('march'),
        t('april'),
        t('may'),
        t('june'),
        t('july'),
        t('august'),
        t('september'),
        t('october'),
        t('november'),
        t('december')
    ];
};

export const getDays = (t: TFunction): string[] => {
    return [
        t('monday'),
        t('tuesday'),
        t('wednesday'),
        t('thursday'),
        t('friday'),
        t('saturday'),
        t('sunday')
    ];
};

export const getRecoveryDate = (
    t: TFunction,
    date: Timestamp,
    dayNumber: number
): string | undefined => {
    const months = getMonths(t);
    const day = getDays(t);
    if (date) {
        const dayNum = date.toDate().getDate();
        return `${day[dayNumber - 1]} ${dayNum} ${months[date.toDate().getMonth()]}`;
    }
};

export const getInitials = (firstName: string, lastName: string): string => {
    return (
        firstName &&
        lastName &&
        firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase()
    );
};

export const getSalesTableData = (results: Commandes[]): Commandes[] => {
    const list: Commandes[] = [];
    results.forEach((result) => {
        if (result.originalOrder) {
            list.push(result);
        }
    });
    return list;
};

export const isPhoneNumberValidate = (number: string): boolean => {
    const pattern = new RegExp(/^[0-9]{9}$/i);
    const patternWithCountryCode = new RegExp(/^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}/);
    if (pattern.test(number) || patternWithCountryCode.test(number)) {
        return true;
    }
    return false;
};

export const weeksBetween = (startDate: Date, endDate: Date): number => {
    return Math.round((endDate.getTime() - startDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
};

export const isDayIncluded = (startDay: number, dayOfWeek: number): boolean => {
    return startDay <= dayOfWeek;
};

export const getDayIndex = (t: TFunction, month: string): number => {
    return getDays(t).indexOf(month) ?? 0;
};

export const sortByDate = (result: ClientTableData[]): ClientTableData[] => {
    return result.sort((a, b) => {
        if (a.sortDate && b.sortDate) {
            const keyA = a.sortDate?.seconds;
            const keyB = b.sortDate?.seconds;
            if (keyA && keyB && keyA > keyB) return -1;
            if (keyA && keyB && keyA < keyB) return 1;
            return 0;
        }
        return 0;
    });
};

export const sortOrderTable = (result: SalesTableViewData[]): SalesTableViewData[] => {
    return result.sort((a, b) => {
        if (a.sortDate && b.sortDate) {
            const keyA = a.sortDate?.seconds;
            const keyB = b.sortDate?.seconds;
            if (keyA && keyB && keyA > keyB) return -1;
            if (keyA && keyB && keyA < keyB) return 1;
            return 0;
        }
        return 0;
    });
};
